/*
 * Store closed — arrival modal (notification style 6)
 *
 * Open/closed is worked out in the browser, not in Liquid: Shopify's CDN caches
 * section output, so a server-rendered "we're closed" can be served hours after
 * it stopped being true. Everything below runs against the store's own timezone,
 * never the visitor's.
 */
(function () {
  'use strict';

  var DAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  var DAY_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  /*
   * Every way a customer can put something in the cart. Minimog renders add-to-cart
   * in ~9 places (product page, quick view, quick add, sticky bar, bundles, the five
   * card styles), but they all share name="add" or .m-add-to-cart. Buy-it-now is
   * included too — it skips the cart entirely, so leaving it live would defeat this.
   */
  var ORDER_BUTTONS = [
    'button[name="add"]',
    'input[name="add"]',
    '.m-add-to-cart',
    '.shopify-payment-button__button',
    // The homepage/collection accordions render "Add" as a plain <div class="btn
    // js-open-product-modal"> — no name="add", no .m-add-to-cart — and its handler
    // POSTs straight to /cart/add.js. Easy to miss; it is a real ordering path.
    '.js-open-product-modal'
  ].join(',');

  /*
   * Catering is deliberately out of scope. Page-level exclusion happens in Liquid,
   * but a catering quick-add can also appear inside an otherwise ordinary grid, so
   * anything under these roots is exempted individually too.
   */
  var CATERING_ROOTS = [
    '.catering-quickview',
    '.catering-grid',
    '.catering-fields',
    '[data-catering-quickview]',
    '.js-open-catering-popup'
  ].join(',');

  var EXEMPT_CLASS = 'store-closed-exempt';
  var FADE_TARGETS = '.m-product-card__media, .m-product-card__content, .m-product-card__info';

  var CLOSED_CLASS = 'store-is-closed';
  var MARKER = 'storeClosedDisabled'; // dataset key, so we only re-enable what we disabled

  /*
   * Testing hatch: ?store_closed_preview=1 forces the closed state regardless of the
   * clock, so the modal and grey-out can be checked during opening hours. Read-only
   * and opt-in per URL — a normal visitor never triggers it.
   */
  function previewForcedClosed() {
    try {
      return new URLSearchParams(window.location.search).get('store_closed_preview') === '1';
    } catch (e) {
      return false;
    }
  }

  /* "07:00-21:00" -> {open: 420, close: 1260}. Blank/"closed"/garbage -> null. */
  function parseRange(value) {
    if (!value) return null;
    var raw = String(value).trim().toLowerCase();
    if (!raw || raw === 'closed') return null;

    var m = raw.match(/^(\d{1,2}):(\d{2})\s*-\s*(\d{1,2}):(\d{2})$/);
    if (!m) return null;

    var open = parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
    var close = parseInt(m[3], 10) * 60 + parseInt(m[4], 10);
    if (open > 1440 || close > 1440 || open === close) return null;

    return { open: open, close: close };
  }

  /* Current weekday + minutes-since-midnight in the store's timezone. */
  function nowInStore(timezone) {
    var parts = {};
    try {
      var fmt = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        weekday: 'short',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
      fmt.formatToParts(new Date()).forEach(function (p) {
        parts[p.type] = p.value;
      });
    } catch (e) {
      return null; // unrecognised timezone — caller stays silent rather than guessing
    }

    var day = DAY_SHORT.indexOf(parts.weekday);
    var hour = parseInt(parts.hour, 10);
    var minute = parseInt(parts.minute, 10);
    if (day === -1 || isNaN(hour) || isNaN(minute)) return null;
    if (hour === 24) hour = 0; // some engines report midnight as 24 under hour12:false

    return { day: day, minutes: hour * 60 + minute };
  }

  /*
   * A range whose close is <= its open spans midnight (e.g. 21:00-02:00), so it
   * has to be checked against both today and yesterday.
   */
  function isOpenNow(hours, day, minutes) {
    var today = hours[day];
    if (today) {
      if (today.close > today.open) {
        if (minutes >= today.open && minutes < today.close) return true;
      } else if (minutes >= today.open) {
        return true;
      }
    }

    var yesterday = hours[(day + 6) % 7];
    if (yesterday && yesterday.close <= yesterday.open && minutes < yesterday.close) return true;

    return false;
  }

  /* Next time the doors open, scanning forward a full week. */
  function nextOpening(hours, day, minutes) {
    for (var i = 0; i < 8; i++) {
      var d = (day + i) % 7;
      var range = hours[d];
      if (!range) continue;
      if (i === 0 && minutes >= range.open) continue; // today's opening has already passed
      return { offset: i, day: d, minutes: range.open };
    }
    return null;
  }

  function formatTime(minutes) {
    var h24 = Math.floor(minutes / 60) % 24;
    var m = minutes % 60;
    var suffix = h24 < 12 ? 'AM' : 'PM';
    var h = h24 % 12;
    if (h === 0) h = 12;
    return h + ':' + (m < 10 ? '0' + m : m) + ' ' + suffix;
  }

  /*
   * Just the time — no "tomorrow" or "on Monday" qualifier. The copy reads
   * "We'll be open at 7:00 AM" whether the next opening is later today or
   * tomorrow morning. Opening time is the same every day, so the day adds
   * nothing for a customer standing outside at 8pm.
   */
  function openingLabel(next, todayIndex) {
    if (!next) return '';
    return formatTime(next.minutes);
  }

  class StoreClosedModal extends HTMLElement {
    connectedCallback() {
      this.modal = this.querySelector('.m-modal');
      if (!this.modal) return;

      this.storageKey = 'Minimog:store-closed-' + this.id;
      this.onKeydown = this.handleKeydown.bind(this);

      this.querySelectorAll('[data-store-closed-dismiss]').forEach(
        function (button) {
          button.addEventListener('click', function (event) {
            event.preventDefault();
            this.dismiss();
          }.bind(this));
        }.bind(this)
      );

      // Click on the backdrop itself (not the card) closes it.
      this.modal.addEventListener('click', function (event) {
        if (event.target === this.modal) this.dismiss();
      }.bind(this));

      if (window.Shopify && Shopify.designMode) {
        document.addEventListener('shopify:section:select', this.onSectionSelect.bind(this));
        document.addEventListener('shopify:section:load', this.onSectionLoad.bind(this));
        if (this.dataset.designMode === 'true') this.previewOpen();
        return;
      }

      this.evaluate();
    }

    get hours() {
      return (this.dataset.hours || '').split('|').map(parseRange);
    }

    evaluate() {
      // Liquid decides scope per template; catering pages arrive with active="false".
      if (this.dataset.active === 'false') return;

      var now = nowInStore(this.dataset.timezone);
      if (!now) return; // unrecognised timezone: fail open, never block ordering on a guess

      var forced = previewForcedClosed();
      var hours = this.hours;

      if (!forced && isOpenNow(hours, now.day, now.minutes)) {
        this.clearClosedState();
        return;
      }

      // Greying and disabling apply on every page view while shut — independently of
      // whether the modal is enabled, and they survive the customer dismissing it.
      if (this.dataset.disableOrdering === 'true') this.applyClosedState();

      if (this.dataset.enable !== 'true') return;
      // In preview the modal must show every time, ignoring an earlier dismissal.
      if (!forced && this.alreadyDismissed()) return;

      this.applyOpeningTime(nextOpening(hours, now.day, now.minutes), now.day);
      this.open();
    }

    /* Mark every catering control so the CSS and the sweep both skip it. */
    exemptCatering() {
      document.querySelectorAll(CATERING_ROOTS).forEach(function (root) {
        if (root.matches(ORDER_BUTTONS)) root.classList.add(EXEMPT_CLASS);
        root.querySelectorAll(ORDER_BUTTONS).forEach(function (b) { b.classList.add(EXEMPT_CLASS); });
        root.querySelectorAll(FADE_TARGETS).forEach(function (n) { n.classList.add(EXEMPT_CLASS); });
      });
    }

    applyClosedState() {
      document.documentElement.classList.add(CLOSED_CLASS);
      this.exemptCatering();
      this.installBlockers();
      this.disableOrderButtons();

      // Quick view, quick add, AJAX cart and lazy-loaded grids inject buttons after
      // this runs, so watch for them instead of only sweeping once.
      if (this.observer || !document.body) return;
      this.observer = new MutationObserver(function () {
        if (this.sweepQueued) return;
        this.sweepQueued = true;
        requestAnimationFrame(function () {
          this.sweepQueued = false;
          this.disableOrderButtons();
        }.bind(this));
      }.bind(this));
      // attributeFilter catches the theme re-enabling a button we already disabled;
      // our own re-disable settles on the next pass instead of looping.
      this.observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['disabled']
      });
    }

    /*
     * The disabled attribute alone is not enough: <product-form> re-enables its own
     * submit button roughly 50ms after we disable it, and re-disabling on every
     * mutation just starts a tug of war. Blocking the click and the submit in the
     * capture phase is authoritative — it runs before any theme handler, and there
     * is nothing for the theme to undo.
     */
    installBlockers() {
      if (this.blockersInstalled) return;
      this.blockersInstalled = true;

      this.blockClick = function (event) {
        var target = event.target;
        if (!target || typeof target.closest !== 'function') return;
        var btn = target.closest(ORDER_BUTTONS);
        if (!btn) return;
        if (btn.classList.contains(EXEMPT_CLASS) || btn.closest(CATERING_ROOTS)) return;
        event.preventDefault();
        event.stopImmediatePropagation();
      };

      this.blockSubmit = function (event) {
        var form = event.target;
        if (!form || !form.action) return;
        if (form.action.indexOf('/cart/add') === -1) return;
        if (form.closest && form.closest(CATERING_ROOTS)) return; // catering: never blocked
        event.preventDefault();
        event.stopImmediatePropagation();
      };

      document.addEventListener('click', this.blockClick, true);
      document.addEventListener('submit', this.blockSubmit, true);
    }

    removeBlockers() {
      if (!this.blockersInstalled) return;
      document.removeEventListener('click', this.blockClick, true);
      document.removeEventListener('submit', this.blockSubmit, true);
      this.blockersInstalled = false;
    }

    clearClosedState() {
      document.documentElement.classList.remove(CLOSED_CLASS);
      this.removeBlockers();
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
      document.querySelectorAll('[data-store-closed-disabled]').forEach(function (el) {
        delete el.dataset[MARKER];
        el.disabled = false;
        el.removeAttribute('aria-disabled');
      });
    }

    disableOrderButtons() {
      this.exemptCatering();
      document.querySelectorAll(ORDER_BUTTONS).forEach(function (el) {
        if (el.classList.contains(EXEMPT_CLASS)) return; // catering: never touched
        if (el.dataset[MARKER]) {
          // Minimog's spinner-button and variant/swatch handlers re-enable buttons
          // after their own async work finishes, undoing us. Put it back.
          if (!el.disabled) {
            el.disabled = true;
            el.setAttribute('aria-disabled', 'true');
          }
          return;
        }
        // Already disabled for another reason (sold out, unavailable variant) —
        // leave it alone so re-opening doesn't wrongly enable it.
        if (el.disabled) return;

        el.dataset[MARKER] = '1';
        el.disabled = true;
        el.setAttribute('aria-disabled', 'true');
      });
    }

    /* Design mode never computes hours — the merchant needs to see the card regardless. */
    previewOpen() {
      var now = nowInStore(this.dataset.timezone);
      if (now) this.applyOpeningTime(nextOpening(this.hours, now.day, now.minutes), now.day);
      this.open();
    }

    applyOpeningTime(next, todayIndex) {
      var label = openingLabel(next, todayIndex);

      this.querySelectorAll('[data-store-closed-token]').forEach(function (el) {
        if (!el.dataset.template) el.dataset.template = el.innerHTML;

        var filled = el.dataset.template.split('[opening_time]').join(
          label ? '<b>' + label + '</b>' : ''
        );
        // Collapse the double space left behind when there is no opening to name.
        el.innerHTML = label ? filled : filled.replace(/\s{2,}/g, ' ');
      });
    }

    alreadyDismissed() {
      if (this.dataset.frequency === 'always') return false;
      try {
        return sessionStorage.getItem(this.storageKey) === '1';
      } catch (e) {
        return false; // private browsing — show it rather than suppress it
      }
    }

    markDismissed() {
      if (this.dataset.frequency === 'always') return;
      try {
        sessionStorage.setItem(this.storageKey, '1');
      } catch (e) {
        /* storage unavailable; the modal simply reappears next page load */
      }
    }

    handleKeydown(event) {
      if (event.key === 'Escape') this.dismiss();
    }

    open() {
      this.modal.style.setProperty('--m-opacity', '1');
      this.modal.classList.add('m-open-modal');
      document.documentElement.classList.add('prevent-scroll');
      document.addEventListener('keydown', this.onKeydown);

      // Move focus into the dialog without visibly highlighting "dismiss" as the
      // first thing the customer sees.
      var card = this.querySelector('.m-modal--content');
      if (card) card.focus();
    }

    close() {
      this.modal.classList.remove('m-open-modal');
      this.modal.style.setProperty('--m-opacity', '0');
      document.documentElement.classList.remove('prevent-scroll');
      document.removeEventListener('keydown', this.onKeydown);
    }

    dismiss() {
      this.markDismissed();
      this.close();
    }

    onSectionLoad(event) {
      if (event.detail.sectionId === this.dataset.sectionId && this.dataset.designMode === 'true') {
        this.previewOpen();
      }
    }

    onSectionSelect(event) {
      if (event.detail.sectionId !== this.dataset.sectionId) return;
      if (this.dataset.designMode === 'true') {
        this.previewOpen();
      } else {
        this.close();
      }
    }
  }

  customElements.define('m-store-closed-modal', StoreClosedModal);
})();
